/* THE MACROS TAB                                                    (PRD 5, SCOPE B3)
 *
 * The v1 calculator, back on the member page. It opens already worked out: weight
 * and body fat from the latest InBody scan, the activity level from how often they
 * have actually trained, the direction from their goal. A coach adjusts from there
 * and saves; nothing is asked of the member.
 *
 * TWO CARDS, inputs and answer, and the answer carries its arithmetic. A target a
 * coach cannot trace back to a scan is a target they cannot defend to the member.
 *
 * STALE IS SAID, not hidden. Targets set before the latest scan were worked from a
 * body that has since changed, which is exactly what the program progress score
 * marks down, so the tab says so and offers the newer figures.
 *
 * Meal plans are not built here yet. That is the rest of B3.
 */
import { useMemo, useState } from 'react';
import {
  ACTIVITY_LEVELS, FAT_PCT, LB_PER_KG, MACRO_GOALS, PROTEIN_PER_LB,
  activityFor, computeMacros, macroGoalFor, toTargets,
  type MacroGoal, type MacroInputs, type Member, type Scan,
} from '@acrostic/model';
import { Panel, Stat } from '../../components/Panel.js';
import { Field } from '../../components/Modal.js';
import { useSource } from '../../data/SourceProvider.js';
import { useToast } from '../../components/Toast.js';
import { fmt } from '../../lib/fmt.js';

type Unit = 'lb' | 'kg';

/* The form holds strings, because a half-typed "7." is not a number yet and a
   field that rewrites itself under the cursor is unusable. */
interface Form {
  weight: string; bodyFat: string;
  height: string; age: string; sex: '' | 'male' | 'female';
  activity: string; goal: MacroGoal; protein: string; fatPct: string;
}

const num = (s: string): number | null => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};
const trim = (n: number, dp: number) => String(+n.toFixed(dp));
/* Within a rounding step. A scan reads 82.37 kg, the form shows 181.6 lb, and
   the round trip back must still count as the scan's own number. */
const near = (a: number | null, b: number | null, tol: number) =>
  a == null || b == null ? a === b : Math.abs(a - b) < tol;

function formFrom(i: MacroInputs, unit: Unit): Form {
  return {
    weight: trim(unit === 'lb' ? i.weightKg * LB_PER_KG : i.weightKg, 1),
    bodyFat: i.bodyFatPct == null ? '' : String(i.bodyFatPct),
    height: i.heightCm ? String(i.heightCm) : '', age: i.age ? String(i.age) : '', sex: i.sex ?? '',
    activity: String(i.activity), goal: i.goal,
    protein: trim(unit === 'lb' ? i.proteinPerLb : i.proteinPerLb * LB_PER_KG, 2),
    fatPct: String(i.fatPct),
  };
}

function inputsFrom(f: Form, unit: Unit): MacroInputs {
  const w = num(f.weight) ?? 0;
  const p = num(f.protein) ?? PROTEIN_PER_LB.default;
  return {
    weightKg: +(unit === 'lb' ? w / LB_PER_KG : w).toFixed(1),
    bodyFatPct: num(f.bodyFat),
    heightCm: num(f.height), age: num(f.age), sex: f.sex || null,
    activity: num(f.activity) ?? 1.55, goal: f.goal,
    proteinPerLb: +(unit === 'lb' ? p : p / LB_PER_KG).toFixed(2),
    fatPct: num(f.fatPct) ?? FAT_PCT.default,
  };
}

/** What the tab opens on when nothing current is saved: the scan, their training, their goal. */
function fresh(m: Member, scan: Scan | null): MacroInputs {
  return {
    weightKg: scan?.weight ?? 0, bodyFatPct: scan?.pbf ?? null,
    activity: activityFor(m.visits30d), goal: macroGoalFor(m.goal),
    proteinPerLb: PROTEIN_PER_LB.default, fatPct: FAT_PCT.default,
  };
}

export function Macros({ m, scans, onChanged }: {
  m: Member; scans: Scan[] | undefined; onChanged: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const saved = m.macros ?? null;
  const latest = scans?.length ? scans[scans.length - 1]! : null;
  const lastScanAt = latest?.date ?? m.lastScanAt ?? null;
  const stale = saved != null && lastScanAt != null && (m.macrosAt ?? saved.setAt) < lastScanAt;
  const start = saved && !stale ? saved : fresh(m, latest);

  const [unit, setUnit] = useState<Unit>('lb');
  const [f, setF] = useState<Form>(() => formFrom(start, 'lb'));
  const [busy, setBusy] = useState(false);

  const inputs = useMemo(() => inputsFrom(f, unit), [f, unit]);
  const res = useMemo(() => computeMacros(inputs), [inputs]);
  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  function switchUnit(u: Unit) {
    if (u === unit) return;
    setF(formFrom(inputsFrom(f, unit), u));
    setUnit(u);
  }

  /* The inputs came off a scan only while weight and body fat still read what the
     scan says. Once a coach types over them, they are the coach's numbers. */
  const basis = saved && !stale ? { date: saved.scanDate, weight: saved.weightKg, pbf: saved.bodyFatPct }
    : latest ? { date: latest.date, weight: latest.weight ?? null, pbf: latest.pbf ?? null } : null;
  const fromScan = basis?.date != null && near(basis.weight, inputs.weightKg, 0.1)
    && near(basis.pbf ?? null, inputs.bodyFatPct, 0.05) ? basis.date : null;

  const unchanged = saved != null && !stale && !('missing' in res)
    && (['weightKg', 'bodyFatPct', 'activity', 'goal', 'proteinPerLb', 'fatPct'] as const)
      .every((k) => typeof saved[k] === 'number' && typeof inputs[k] === 'number'
        ? near(saved[k] as number, inputs[k] as number, k === 'weightKg' ? 0.1 : 0.005)
        : saved[k] === inputs[k]);

  async function save() {
    if ('missing' in res || busy) return;
    setBusy(true);
    try {
      const { setAt: _a, by: _b, ...t } = toTargets(inputs, res, { setAt: '', by: null, scanDate: fromScan });
      await source.setMemberMacros(m.id, t);
      toast(`Targets saved on ${m.firstName}'s record`, 'good');
      onChanged();
    } catch (e) {
      toast((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const perWeek = ((m.visits30d ?? 0) * 7 / 30).toFixed(1);
  const noBodyFat = inputs.bodyFatPct == null;

  return (
    <div className="grid2">
      <Panel
        eyebrow="Starting points, not prescriptions" title="Macros"
        right={
          <div className="seg" role="group" aria-label="Units">
            {(['lb', 'kg'] as const).map((u) => (
              <button key={u} type="button" className={'segbtn t-label-small' + (unit === u ? ' on' : '')}
                aria-pressed={unit === u} onClick={() => switchUnit(u)}>{u}</button>
            ))}
          </div>
        }
      >
        {saved && stale ? (
          <p className="notice warn t-body-small">
            Targets were set {fmt.date(saved.setAt)}, before the scan of {fmt.date(lastScanAt)}.
            The figures here are worked from the newer scan. Save them to bring the targets up to date.
          </p>
        ) : saved ? (
          <p className="notice t-body-small">
            Set {fmt.date(saved.setAt)}{saved.by ? ` by ${saved.by}` : ''}
            {saved.scanDate ? `, from the scan of ${fmt.date(saved.scanDate)}` : ', from a weight typed in'}.
          </p>
        ) : (
          <p className="notice t-body-small">
            No targets saved for {m.firstName} yet.{' '}
            {latest ? `Worked out below from the scan of ${fmt.date(latest.date)}.` : 'There is no scan on record, so enter a weight to start.'}
          </p>
        )}

        <div className="macroform">
          <Field label={`Bodyweight (${unit})`}>
            <input className="field" type="number" inputMode="decimal" min="0" step="0.1"
              value={f.weight} onChange={set('weight')} />
          </Field>
          <Field label="Body fat (%)" {...(noBodyFat ? { hint: 'Without it, BMR needs a height, age and sex.' } : {})}>
            <input className="field" type="number" inputMode="decimal" min="0" max="70" step="0.1"
              value={f.bodyFat} onChange={set('bodyFat')} />
          </Field>

          {noBodyFat && (
            <>
              <Field label="Height (cm)">
                <input className="field" type="number" min="0" step="0.5" value={f.height} onChange={set('height')} />
              </Field>
              <Field label="Age">
                <input className="field" type="number" min="0" max="120" step="1" value={f.age} onChange={set('age')} />
              </Field>
              <Field label="Sex">
                <select className="field" value={f.sex} onChange={set('sex')}>
                  <option value="">Choose</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
              </Field>
            </>
          )}

          <Field label="Activity" hint={`${m.visits30d ?? 0} visits in the last 30 days, about ${perWeek} a week.`}>
            <select className="field" value={f.activity} onChange={set('activity')}>
              {ACTIVITY_LEVELS.map((a) => <option key={a.factor} value={String(a.factor)}>{a.label}</option>)}
            </select>
          </Field>
          <Field label="Goal" {...(m.goal ? { hint: `Their stated goal: ${m.goal}.` } : {})}>
            <select className="field" value={f.goal} onChange={set('goal')}>
              {(Object.keys(MACRO_GOALS) as MacroGoal[]).map((g) => (
                <option key={g} value={g}>{MACRO_GOALS[g].label}</option>
              ))}
            </select>
          </Field>
          <Field label={`Protein (g per ${unit} of bodyweight)`}
            hint={unit === 'lb' ? 'Usually 0.7 to 1.2.' : 'Usually 1.6 to 2.6.'}>
            <input className="field" type="number" inputMode="decimal" step="0.05"
              value={f.protein} onChange={set('protein')} />
          </Field>
          <Field label="Fat (% of calories)" hint="Carbohydrate takes what is left.">
            <input className="field" type="number" min={FAT_PCT.min} max={FAT_PCT.max} step="1"
              value={f.fatPct} onChange={set('fatPct')} />
          </Field>
        </div>
      </Panel>

      <Panel eyebrow={'missing' in res ? 'Not enough to work from' : `BMR by ${res.bmrMethod}`} title="Daily targets">
        {'missing' in res ? (
          <p className="none t-body-medium">Enter {res.missing} to work out targets for {m.firstName}.</p>
        ) : (
          <>
            <div className="stats macrostats">
              <Stat value={Math.round(res.calories).toLocaleString()} label="Calories (kcal)" />
              <Stat value={`${Math.round(res.proteinG)} g`} label={`Protein, ${res.share.protein}%`} />
              <Stat value={`${Math.round(res.carbG)} g`} label={`Carbohydrate, ${res.share.carb}%`} />
              <Stat value={`${Math.round(res.fatG)} g`} label={`Fat, ${res.share.fat}%`} />
            </div>
            {res.carbsSqueezed && (
              <p className="notice warn t-body-small" style={{ marginTop: 12 }}>
                Protein and fat use up every calorie, so there is no room for carbohydrate. Lower one of them.
              </p>
            )}
            <div className="deflist" style={{ marginTop: 16 }}>
              {res.working.map(([k, v]) => (
                <div className="defrow t-body-small" key={k}>
                  <span className="muted">{k}</span><span>{v}</span>
                </div>
              ))}
            </div>
            <div className="macrosave">
              <button type="button" className="btn primary t-label-medium"
                disabled={busy || unchanged} onClick={() => void save()}>
                {unchanged ? 'Saved' : saved ? 'Save as the new targets' : 'Save these targets'}
              </button>
              <span className="t-caption muted">
                Saving lands on the timeline with your name and today&rsquo;s date.
              </span>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}
